import { useCallback, useEffect, useState } from "react";

import { HttpApiService } from "@/shared/services/httpApi.service";

import { VerificationService } from "../services/verificationService";
import type { VerificationSessionResponse } from "../types/verification-api.types";

/** Why the session couldn't be resolved — drives the full-screen fallback in marco B. */
export type VerificationSessionErrorKind = "expired" | "invalid" | "generic";

export interface UseVerificationSessionResult {
  session: VerificationSessionResponse | null;
  isLoading: boolean;
  errorKind: VerificationSessionErrorKind | null;
  reload: () => void;
}

/**
 * Resolves the identity token of the link into the verification session (purpose, subject, status, link
 * expiry). A 401 means the link token is expired/rejected by the guard; a 404 means the link is invalid.
 */
export function useVerificationSession(token: string): UseVerificationSessionResult {
  const [session, setSession] = useState<VerificationSessionResponse | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [errorKind, setErrorKind] = useState<VerificationSessionErrorKind | null>(null);

  const load = useCallback(async () => {
    setIsLoading(true);
    setErrorKind(null);
    try {
      const { data } = await VerificationService.getSession(token);
      setSession(data);
    } catch (error) {
      if (HttpApiService.isUnauthorizedError(error)) {
        setErrorKind("expired");
      } else if (HttpApiService.isNotFoundError(error)) {
        setErrorKind("invalid");
      } else {
        setErrorKind("generic");
      }
      setSession(null);
    } finally {
      setIsLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void load();
  }, [load]);

  return { session, isLoading, errorKind, reload: load };
}
